/**
 * Life4Earth V2 — Admin Project Cleanup (Hard Delete Utility)
 *
 * Search across BOTH full projects and mini-projects (all statuses, including
 * archived), and permanently hard-delete one plus all dependent data.
 * Full projects → DELETE /api/projects/{id}/hard.
 * Mini-projects → DELETE /api/mini-projects/{guid}/hard (system-admin only;
 *   child rows cascade per migration 228).
 *
 * English only (system admin UI per CLAUDE.md).
 */

import React, { useState, useCallback, useRef } from 'react';
import { fetchAllProjects, hardDeleteProject, ProjectResponse } from '../../api/projects';
import { fetchMiniProjects, hardDeleteMiniProject } from '../../api/miniProjects';
import styles from './AdminPages.module.css';

// ── Types ────────────────────────────────────────────────────

interface DeleteResult {
  message: string;
  recordsRemoved: Record<string, number>;
}

type CleanupKind = 'full' | 'mini';

interface CleanupRow {
  kind: CleanupKind;
  id: string;            // full: projects.id; mini: mini_projects.mini_project_guid
  name: string;
  status: string;
  category: string;
  community: string;
  createdAt: string | null;
}

// ── Component ────────────────────────────────────────────────

export default function AdminPillars() {
  const [rows, setRows] = useState<CleanupRow[]>([]);
  const [counts, setCounts] = useState<{ full: number; mini: number }>({ full: 0, mini: 0 });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searched, setSearched] = useState(false);

  // Delete confirmation state
  const [confirmTarget, setConfirmTarget] = useState<CleanupRow | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState<DeleteResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const searchRef = useRef<HTMLInputElement>(null);

  // ── Fetch full projects + mini-projects ─────────────────────

  const handleSearch = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDeleteResult(null);
    try {
      const [full, mini] = await Promise.all([
        fetchAllProjects(),
        fetchMiniProjects({ includeArchived: true }),
      ]);
      const fullRows: CleanupRow[] = full.map((p) => ({
        kind: 'full',
        id: p.id,
        name: p.name,
        status: p.status || '',
        category: p.projectCategory || '',
        community: p.communityDisplay || '',
        createdAt: p.createdAt || null,
      }));
      const miniRows: CleanupRow[] = mini.map((m) => ({
        kind: 'mini',
        id: m.miniProjectGuid,
        name: m.title,
        status: m.status || '',
        category: m.categoryKey || '',
        community: '',
        createdAt: m.createdAt || null,
      }));
      const all = [...fullRows, ...miniRows].sort((a, b) => a.name.localeCompare(b.name));
      setRows(all);
      setCounts({ full: fullRows.length, mini: miniRows.length });
      setSearched(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSearch();
    },
    [handleSearch],
  );

  // ── Filter ──────────────────────────────────────────────────

  const filtered = rows.filter((r) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      r.name.toLowerCase().includes(q) ||
      r.id.toLowerCase().includes(q) ||
      r.status.toLowerCase().includes(q) ||
      r.community.toLowerCase().includes(q) ||
      r.category.toLowerCase().includes(q) ||
      (r.kind === 'mini' ? 'mini' : 'full').includes(q)
    );
  });

  // ── Hard delete (routed by kind) ────────────────────────────

  const handleDelete = useCallback(async () => {
    if (!confirmTarget) return;
    setDeleting(true);
    setError(null);
    setDeleteResult(null);
    try {
      if (confirmTarget.kind === 'full') {
        const result = await hardDeleteProject(confirmTarget.id);
        setDeleteResult({ message: result.message, recordsRemoved: result.recordsRemoved });
      } else {
        await hardDeleteMiniProject(confirmTarget.id);
        setDeleteResult({
          message: `Mini-project "${confirmTarget.name}" permanently deleted (child data cascaded).`,
          recordsRemoved: {},
        });
      }
      setRows((prev) => prev.filter((r) => r.id !== confirmTarget.id));
      setCounts((prev) => ({
        full: prev.full - (confirmTarget.kind === 'full' ? 1 : 0),
        mini: prev.mini - (confirmTarget.kind === 'mini' ? 1 : 0),
      }));
      setConfirmTarget(null);
      setConfirmText('');
    } catch (err: any) {
      setError(err?.message || 'Hard delete failed');
    } finally {
      setDeleting(false);
    }
  }, [confirmTarget]);

  const openConfirm = useCallback((r: CleanupRow) => {
    setConfirmTarget(r);
    setConfirmText('');
    setDeleteResult(null);
    setError(null);
  }, []);

  const closeConfirm = useCallback(() => {
    setConfirmTarget(null);
    setConfirmText('');
  }, []);

  // ── Status badge ────────────────────────────────────────────

  const statusColor = (s: string) => {
    switch (s) {
      case 'active':              return '#166534';
      case 'paused':              return '#92400e';
      case 'expired':             return '#92400e';
      case 'completed':
      case 'complete':            return '#1e40af';
      case 'archived':
      case 'draft':               return '#6b7280';
      default:                    return '#6b7280';
    }
  };

  const statusBg = (s: string) => {
    switch (s) {
      case 'active':              return '#dcfce7';
      case 'paused':              return '#fef3c7';
      case 'expired':             return '#fef3c7';
      case 'completed':
      case 'complete':            return '#dbeafe';
      case 'archived':
      case 'draft':               return '#f3f4f6';
      default:                    return '#f3f4f6';
    }
  };

  // ── Render ──────────────────────────────────────────────────

  return (
    <div>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Project Cleanup</h1>
          <p className={styles.pageSubtitle}>
            Search full projects and mini-projects across all statuses (including archived) and
            permanently delete them with all child data. This action is irreversible.
          </p>
        </div>
      </div>

      {/* Status messages */}
      {error && (
        <div className={`${styles.statusBar} ${styles.status_error}`}>
          {error}
        </div>
      )}
      {deleteResult && (
        <div className={`${styles.statusBar} ${styles.status_success}`}>
          {deleteResult.message}
          {Object.keys(deleteResult.recordsRemoved).length > 0 && (
            <>
              {' — '}Records removed:{' '}
              {Object.entries(deleteResult.recordsRemoved)
                .filter(([, v]) => v > 0)
                .map(([k, v]) => `${k}: ${v}`)
                .join(', ') || 'none'}
            </>
          )}
        </div>
      )}

      {/* Search bar */}
      <div className={styles.section}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            ref={searchRef}
            className={styles.input}
            type="text"
            placeholder="Search by name, GUID, status, community, category, or type (full / mini)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            style={{ flex: 1 }}
          />
          <button
            className={styles.btnPrimary}
            onClick={handleSearch}
            disabled={loading}
          >
            {loading ? 'Loading...' : searched ? 'Refresh' : 'Load Projects'}
          </button>
        </div>
        {searched && (
          <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 8, marginBottom: 0 }}>
            {filtered.length} of {rows.length} shown · {counts.full} full, {counts.mini} mini
          </p>
        )}
      </div>

      {/* List */}
      {searched && filtered.length === 0 && !loading && (
        <p className={styles.emptyMsg}>
          {rows.length === 0
            ? 'No projects found in the database.'
            : 'No projects match your search.'}
        </p>
      )}

      {filtered.length > 0 && (
        <div className={styles.section} style={{ padding: 0 }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 64 }}>Type</th>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Community</th>
                <th style={thStyle}>Created</th>
                <th style={{ ...thStyle, width: 60, textAlign: 'center' }}>Delete</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={`${r.kind}-${r.id}`} style={trStyle}>
                  <td style={tdStyle}>
                    <span style={r.kind === 'mini' ? miniBadgeStyle : fullBadgeStyle}>
                      {r.kind === 'mini' ? 'Mini' : 'Full'}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1a2e' }}>
                      {r.name}
                    </div>
                    <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>
                      {r.id}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    {r.status ? (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: 4,
                          color: statusColor(r.status),
                          background: statusBg(r.status),
                        }}
                      >
                        {r.status}
                      </span>
                    ) : '—'}
                  </td>
                  <td style={{ ...tdStyle, fontSize: 13, color: '#4b5563' }}>
                    {r.category || '—'}
                  </td>
                  <td style={{ ...tdStyle, fontSize: 13, color: '#4b5563' }}>
                    {r.community || '—'}
                  </td>
                  <td style={{ ...tdStyle, fontSize: 12, color: '#6b7280' }}>
                    {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button
                      onClick={() => openConfirm(r)}
                      style={deleteBtnStyle}
                      title={`Permanently delete "${r.name}"`}
                    >
                      &times;
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirmation dialog (modal overlay) */}
      {confirmTarget && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: '#991b1b' }}>
              Permanently Delete {confirmTarget.kind === 'mini' ? 'Mini-Project' : 'Project'}
            </h3>
            <p style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.5, margin: '0 0 12px' }}>
              This will permanently delete <strong>{confirmTarget.name}</strong> and ALL
              associated data{' '}
              {confirmTarget.kind === 'mini'
                ? '(goals, tasks, team, BOM items, statuses, roles, milestones, and links).'
                : '(Gantt tasks, KPI data, mission metrics, preflight items, ACLs, role slots, collection links, impact events, and more).'}
            </p>
            <p style={{ fontSize: 13, color: '#991b1b', fontWeight: 600, margin: '0 0 12px' }}>
              This action cannot be undone.
            </p>

            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
              <div><strong>Type:</strong> {confirmTarget.kind === 'mini' ? 'Mini-project' : 'Full project'}</div>
              <div><strong>GUID:</strong> <code style={{ fontSize: 11 }}>{confirmTarget.id}</code></div>
              <div><strong>Status:</strong> {confirmTarget.status || '—'}</div>
              {confirmTarget.community && (
                <div><strong>Community:</strong> {confirmTarget.community}</div>
              )}
            </div>

            <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>
              Type the name to confirm:
            </label>
            <input
              className={styles.input}
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={confirmTarget.name}
              autoFocus
              style={{ marginBottom: 16 }}
            />

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                className={styles.btnSecondary}
                onClick={closeConfirm}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={confirmText !== confirmTarget.name || deleting}
                style={{
                  ...hardDeleteBtnStyle,
                  opacity: confirmText !== confirmTarget.name || deleting ? 0.4 : 1,
                  cursor: confirmText !== confirmTarget.name || deleting ? 'default' : 'pointer',
                }}
              >
                {deleting ? 'Deleting...' : 'Permanently Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Inline styles ────────────────────────────────────────────

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};

const thStyle: React.CSSProperties = {
  textAlign: 'start',
  padding: '10px 16px',
  fontSize: 11,
  fontWeight: 700,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  borderBottom: '1px solid #e5e7eb',
  background: '#f9fafb',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 16px',
  verticalAlign: 'middle',
};

const trStyle: React.CSSProperties = {
  borderBottom: '1px solid #f3f4f6',
};

const fullBadgeStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  padding: '2px 7px',
  borderRadius: 4,
  color: '#3730a3',
  background: '#e0e7ff',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const miniBadgeStyle: React.CSSProperties = {
  ...fullBadgeStyle,
  color: '#9a3412',
  background: '#ffedd5',
};

const deleteBtnStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 18,
  color: '#ef4444',
  background: 'rgba(239, 68, 68, 0.06)',
  border: '1px solid rgba(239, 68, 68, 0.15)',
  borderRadius: 4,
  cursor: 'pointer',
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
};

const modalStyle: React.CSSProperties = {
  background: '#ffffff',
  borderRadius: 10,
  padding: '28px 32px',
  maxWidth: 480,
  width: '90vw',
  boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
};

const hardDeleteBtnStyle: React.CSSProperties = {
  padding: '8px 20px',
  fontSize: 13,
  fontWeight: 600,
  color: '#ffffff',
  background: '#dc2626',
  border: 'none',
  borderRadius: 6,
};
